import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ContentLayout from '../../layout/ContentLayout';
import styles from '../../styles/webshopAndProducts.module.scss';
import GenericTable from '../../components/tables/GenericTable';
import Pagination from '../../components/layout/Pagination';
import { fetchOrders, createOrder, editOrderContents, deleteOrder } from '../../api/webshop-products-orders-api';
import CreateOrderModal from '../../components/modals/CreateOrderModal';
import ExportOrdersModal from '../../components/modals/ExportOrdersModal';
import DeleteModal from '../../components/modals/DeleteModal';
import { exportOrders } from '../../api/orders-api';
import { formatPriceWithCurrency } from '../../utils/formatPrice';
import { formatOrderStatus, formatOrderStatusAsPill, formatOrderChannelAsPill, ORDER_STATUSES } from '../../utils/formatOrderStatus';
import { isOrderEditable } from '../../utils/orderEditability';
import { toast } from 'react-toastify';
import { Button, Input, Tabs } from 'antd';
import Spinner from '../../components/elements/Spinner/Spinner';
import FilterDropdown from '../../components/filters/FilterDropdown';
import OrderEarnings from '../../components/orders/OrderEarnings';
import CharityEarnings from '../../components/charity/CharityEarnings';
import dayjs from 'dayjs';

const OrdersPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(
    parseInt(searchParams.get('page') || '1', 10)
  );
  const [totalPages, setTotalPages] = useState(1);
  const [searchOrder, setSearchOrder] = useState('');
  const [debouncedSearchOrder, setDebouncedSearchOrder] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [orderModalLoading, setOrderModalLoading] = useState(false);
  const [editOrderTarget, setEditOrderTarget] = useState(null); // null => create mode
  const [loading, setLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [loadingDeletingOrder, setLoadingDeletingOrder] = useState(false);

  const activeTab = searchParams.get('tab') || 'pregled';

  const tabItems = [
    { key: 'pregled', label: 'Pregled narudžbina' },
    { key: 'zarada', label: 'Zarada' },
    { key: 'donacije', label: 'Humanitarne donacije' },
  ];

  const handleTabChange = (key) => {
    setCurrentPage(1);
    setSearchParams({ tab: key, page: '1' }, { replace: true });
  };

  useEffect(() => {
    // Merge rather than replace — a bare `{ page }` would drop `tab` and bounce
    // the user back to the orders list.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('page', currentPage);
        return next;
      },
      { replace: true }
    );
  }, [currentPage]); // eslint-disable-line

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchOrder(searchOrder), 500);
    return () => clearTimeout(timer);
  }, [searchOrder]);

  const loadOrders = useCallback(async () => {
    if (activeTab !== 'pregled') return;

    const { data, error } = await fetchOrders({
      page: currentPage,
      limit: 10,
      search: debouncedSearchOrder || null,
      status: statusFilter || null,
    });
    if (!error) {
      setOrders(data.orders);
      setTotalPages(data.totalPages);
    }
    setLoading(false);
  }, [currentPage, debouncedSearchOrder, statusFilter, activeTab]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const closeOrderModal = () => {
    setIsOrderModalOpen(false);
    setEditOrderTarget(null);
  };

  const handleOrderModalConfirm = async (payload) => {
    setOrderModalLoading(true);
    const isEdit = !!editOrderTarget;
    const { error } = isEdit
      ? await editOrderContents(editOrderTarget._id, payload)
      : await createOrder(payload);
    setOrderModalLoading(false);
    if (error) {
      toast.error(error);
      return false;
    }
    toast.success(isEdit ? 'Porudžbina uspešno izmenjena.' : 'Porudžbina uspešno kreirana.');
    closeOrderModal();
    await loadOrders();
    return true;
  };

  const handleExport = async (exportParams) => {
    return exportOrders({
      createdFrom: exportParams.createdFrom,
      createdTo: exportParams.createdTo,
      status: exportParams.status,
    });
  };

  const handleDeleteOrder = async () => {
    toast.loading('Brisanje narudžbine...');
    setLoadingDeletingOrder(true);
    const { success, message } = await deleteOrder(orderToDelete._id);
    toast.dismiss();
    if (!success) {
      toast.error(message || 'Greška pri brisanju narudžbine');
      setLoadingDeletingOrder(false);
    } else {
      setIsDeleteModalOpen(false);
      toast.success('Narudžbina obrisana');
      if (orders.length === 1 && currentPage > 1) {
        setCurrentPage((prev) => prev - 1);
      } else {
        await loadOrders();
      }
      setLoadingDeletingOrder(false);
      setOrderToDelete(null);
    }
  };

  const statusSections = [
    {
      type: 'status',
      title: 'Status porudžbine',
      items: [{ key: null, label: 'Svi statusi' }, ...ORDER_STATUSES],
    },
  ];

  const handleStatusFilterChange = (filter) => {
    setStatusFilter(filter.status || null);
    setCurrentPage(1);
  };

  const columns = [
    { key: 'orderNumber', label: 'Šifra porudžbine' },
    {
      key: 'customerDetails',
      label: 'Kupac',
      render: (val) => <span>{val?.fullName || '-'}</span>,
    },
    {
      key: 'createdAt',
      label: 'Datum porudžbine',
      render: (val) => <span>{dayjs(val).format('DD.MM.YYYY.')}</span>,
    },
    {
      key: 'purchaseDate',
      label: 'Status',
      render: (val, row) => formatOrderStatusAsPill(row.status),
    },
    {
      key: 'channel',
      label: 'Tip kupovine',
      render: (val, row) =>
        formatOrderChannelAsPill(val, row?.referral?.doctor, row?.isMentorPackage),
    },
    {
      key: 'total',
      label: 'Ukupna cena',
      render: (val) => <span>{formatPriceWithCurrency(val || 0)}</span>,
    },
    {
      key: 'payment',
      label: 'ID Transakcije',
      render: (val) => <span>{val?.transaction?.transId || '-'}</span>,
    },
  ];

  return (
    <>
      <ContentLayout pt={true}>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tabItems}
          className="medics_tab"
        />

        {activeTab === 'zarada' ? (
          <OrderEarnings />
        ) : activeTab === 'donacije' ? (
          <CharityEarnings />
        ) : (
          <div className={styles.webshop__orders}>
            <div className={styles.header}>
              {/* The tab label already names this view, so the actions simply
                  keep the right-hand side of the header. */}
              <div className={styles.actions} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginLeft: 'auto' }}>
                <Input
                  placeholder="Pretraži po kupcu, broju porudžbine, email-u, telefonu..."
                  allowClear
                  value={searchOrder}
                  onChange={(e) => { setSearchOrder(e.target.value); setCurrentPage(1); }}
                  style={{ width: 280 }}
                />
                <FilterDropdown
                  sections={statusSections}
                  selectedFilter={{ status: statusFilter }}
                  onFilterChange={handleStatusFilterChange}
                  buttonLabel={statusFilter ? formatOrderStatus(statusFilter) : 'Status'}
                />
                <Button
                  className="btn-primary"
                  onClick={() => { setEditOrderTarget(null); setIsOrderModalOpen(true); }}
                >
                  + Nova porudžbina
                </Button>
                <Button
                  className="btn-tertiary"
                  onClick={() => setIsExportModalOpen(true)}
                  icon={
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M8 12L3 7h3V1h4v6h3L8 12z" />
                      <path d="M1 14h14v2H1z" />
                    </svg>
                  }
                >
                  Izvezi
                </Button>
              </div>
            </div>
            <div className={`${styles.body} products_wrapper`}>
              {loading ? (
                <Spinner size="lg" />
              ) : (
                <>
                  <GenericTable
                    data={orders}
                    columns={columns}
                    actions={[
                      {
                        key: 'view',
                        label: 'Vidi detalje',
                        icon: <img src="/assets/icons/view-icon.svg" alt="View" />,
                        onClick: (order) =>
                          navigate(`/order/${order._id}`, {
                            state: { from: `/narudzbine?page=${currentPage}` },
                          }),
                      },
                      {
                        key: 'edit',
                        label: 'Izmeni narudžbinu',
                        icon: <img src="/assets/icons/edit-icon.svg" alt="Edit" />,
                        visible: (order) => isOrderEditable(order),
                        onClick: (order) => { setEditOrderTarget(order); setIsOrderModalOpen(true); },
                      },
                      {
                        key: 'delete',
                        label: 'Obriši narudžbinu',
                        icon: <img src="/assets/icons/delete.svg" alt="Delete" />,
                        danger: true,
                        onClick: (order) => {
                          setOrderToDelete(order);
                          setIsDeleteModalOpen(true);
                        },
                      },
                    ]}
                    emptyState={{
                      icon: '/assets/icons/no-ads.svg',
                      title: 'Trenutno nema narudžbina',
                    }}
                  />
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </>
              )}
            </div>
          </div>
        )}
      </ContentLayout>
      <ExportOrdersModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={handleExport}
      />
      <CreateOrderModal
        isOpen={isOrderModalOpen}
        onClose={closeOrderModal}
        onConfirm={handleOrderModalConfirm}
        loading={orderModalLoading}
        mode={editOrderTarget ? 'edit' : 'create'}
        order={editOrderTarget}
      />
      <DeleteModal
        visible={isDeleteModalOpen}
        onDelete={handleDeleteOrder}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setOrderToDelete(null);
        }}
        loading={loadingDeletingOrder}
        title="Brisanje narudžbine"
        message={`Da li ste sigurni da želite da obrišete narudžbinu ${orderToDelete?.orderNumber}?`}
        buttonText="Obriši narudžbinu"
        cancelText="Odustani"
      />
    </>
  );
};

export default OrdersPage;
